"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useState } from "react";
import superjson from "superjson";
import { captureException } from "@/lib/sentry/captureClient";
import { isExpectedTrpcError } from "@sum-video/shared/utils/expectedTrpcErrors";
import { SentryTag } from "@/lib/sentry/tags";
import { loadSupabaseClient } from "@/lib/supabase/loadClient";
import { api } from "./client";

function captureUnexpectedQueryError(error: unknown, source: string): void {
  if (isExpectedTrpcError(error)) {
    return;
  }
  captureException(error, { tags: { [SentryTag.Source]: source } });
}

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            captureUnexpectedQueryError(error, "reactQuery");
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            captureUnexpectedQueryError(error, "reactMutation");
          },
        }),
      }),
  );
  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        httpBatchLink({
          url: "/api/trpc",
          transformer: superjson,
          headers: async () => {
            const supabase = await loadSupabaseClient();
            await supabase.auth.getUser();
            const {
              data: { session },
            } = await supabase.auth.getSession();
            if (!session?.access_token) {
              return {};
            }
            return { authorization: `Bearer ${session.access_token}` };
          },
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </api.Provider>
  );
}
